import { useEffect, useState } from 'react';
import { Button, Link, Stack, Tab, TabList, TabPanel, Tabs, Typography } from '@mui/joy';

import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import NotStartedIcon from '@mui/icons-material/NotStarted';
import StopIcon from '@mui/icons-material/Stop';

import { useTranslation } from 'react-i18next';

import { isProblemDetailTextOr } from 'src/common/ProblemDetail';
import { NotificationSeverity, showSnackbar } from 'src/features/notifications/notifications.slice';
import { useAppDispatch } from 'src/redux-app-hooks';

import { VmStateDto, admiPvSolVmService } from './admi-pv-sol-vm.service';

export function PvSolVmPage() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [vmState, setVmState] = useState<VmStateDto>({
        state: 'loading...',
        lastStartedUser: null,
    });
    useEffect(() => {
        const load = () => {
            admiPvSolVmService
                .getVirtualMachineState()
                .then((vmState) => setVmState(vmState))
                .catch(() => {});
        };
        load();
        const intervalId = setInterval(load, 5000);
        return () => clearInterval(intervalId);
    }, []);

    const [isLoadingStart, setIsLoadingStart] = useState<boolean>(false);
    const [isLoadingStop, setIsLoadingStop] = useState<boolean>(false);

    /**
     * Starting or stopping waits for Azure to finish, and Azure can refuse (e.g. no capacity for
     * the VM size). Report that to the user instead of leaving the rejection unhandled, and always
     * release the button's loading state.
     */
    async function runVmAction(
        action: () => Promise<unknown>,
        setIsLoading: (isLoading: boolean) => void,
        failureMessage: (detail: string) => string,
    ) {
        setIsLoading(true);
        try {
            await action();
        } catch (error) {
            console.warn('PvSolVmPage', 'VM action failed', error);
            dispatch(
                showSnackbar({
                    message: failureMessage(isProblemDetailTextOr(error, String(error))),
                    severity: NotificationSeverity.Error,
                }),
            );
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            <Typography level="h3">{t('Virtual Machine: PV*SOL')}</Typography>
            <p>
                {t(
                    'This page allows you to start and stop the virtual machine for PV*SOL. Please make sure that nobody currently uses the virtual machine before stopping it.',
                )}
            </p>

            <Typography level="h4" sx={{ marginTop: 2 }}>
                {t('Manage')}
            </Typography>
            <p>
                <strong>{t('State')}:</strong> {t(vmState.state)}
                <br />
                <strong>
                    {t('Last started by')}: {vmState.lastStartedUser ?? ''}
                </strong>{' '}
                {}
            </p>

            <Stack spacing={1} direction="row">
                <Button
                    startDecorator={<NotStartedIcon />}
                    loading={isLoadingStart}
                    loadingPosition="start"
                    onClick={() =>
                        runVmAction(
                            () => admiPvSolVmService.startVirtualMachine(),
                            setIsLoadingStart,
                            (detail) => t('Could not start the virtual machine: {{detail}}', { detail }),
                        )
                    }
                    // Not gated on 'loading...': polling swallows errors, so a failing state lookup would lock the button.
                    disabled={vmState.state === 'RUNNING' || isLoadingStop}
                >
                    {t('Start Virtual Machine')}
                </Button>
                <Button
                    startDecorator={<StopIcon />}
                    loading={isLoadingStop}
                    loadingPosition="start"
                    onClick={() =>
                        runVmAction(
                            () => admiPvSolVmService.stopVirtualMachine(),
                            setIsLoadingStop,
                            (detail) => t('Could not stop the virtual machine: {{detail}}', { detail }),
                        )
                    }
                    disabled={vmState.state === 'STOPPED' || isLoadingStart}
                >
                    {t('Stop Virtual Machine')}
                </Button>
            </Stack>

            <Typography level="h4" sx={{ marginTop: 2 }}>
                {t('How to connect')}
            </Typography>
            <Tabs>
                <TabList>
                    <Tab variant="soft" color="primary">
                        Windows
                    </Tab>
                    <Tab variant="soft" color="primary">
                        Mac
                    </Tab>
                </TabList>
                <TabPanel value={0}>
                    <Button
                        startDecorator={<ElectricalServicesIcon />}
                        component="a"
                        href={admiPvSolVmService.buildVmConnectRdpLink()}
                    >
                        {t('Connect')}
                    </Button>
                    <p>{t('Password: Ask an admi colleague')}</p>
                </TabPanel>
                <TabPanel value={1}>
                    <p>
                        Download the RDP client from:{' '}
                        <Link href="https://apps.apple.com/ch/app/microsoft-remote-desktop/id1295203466?mt=12">
                            Mac App Store
                        </Link>
                    </p>
                    <p>
                        Connect using the following credentials:
                        <ul>
                            <li>IP/Host: 20.13.160.190</li>
                            <li>{t('User')}: admi</li>
                            <li>{t('Password: Ask an admi colleague')}</li>
                        </ul>
                    </p>
                </TabPanel>
            </Tabs>
        </>
    );
}
